import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Form } from '@openedx/paragon';
import { fieldEvents } from 'utils/eventing';
import { getCorrelationID, hyphenateForTagular } from 'utils/tagular';
import { useCart } from 'frontastic/hooks';
import { FormError } from './form-error';
import { FormSelectProps } from './types';

export const FormSelect = ({
  name,
  label,
  styleClasses,
  options,
  autocomplete,
  disabled,
  onChange,
}: FormSelectProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[name]?.message;
  const hasFiredEvent = useRef(false);
  const { data: cart } = useCart();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!hasFiredEvent.current) {
      const fieldEvent = fieldEvents({
        correlationId: getCorrelationID(),
        userInputField: {
          fieldId: name,
          fieldLabel: name,
          fieldName: name,
          fieldType: name,
          fieldValue: 'SANITIZED',
        },
        formContext: {
          formBrand: hyphenateForTagular(
            cart?.lineItems?.[0]?.variant?.attributes?.['brand-text'] ?? '',
          ),
          formId: cart?.lineItems?.[0]?.variant?.sku,
          formName: cart?.lineItems?.[0]?.name,
          formType: cart?.lineItems?.[0]?.productType?.obj?.name,
        },
      });

      fieldEvent.fieldInputted();
      hasFiredEvent.current = true;
    }

    onChange?.(e.target.value);
  };

  return (
    <Form.Group className={styleClasses} isInvalid={!!error} controlId={name}>
      <Form.Label>{label}</Form.Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <Form.Control
              {...field}
              disabled={disabled}
              autoComplete={autocomplete}
              as="select"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                field.onChange(e);
                handleChange(e);
              }}
            >
              {options?.map((item) => (
                <option key={item.value + item.title} value={item.value}>
                  {item.title}
                </option>
              ))}
            </Form.Control>
            <FormError error={error} value={field.value as string} />
          </>
        )}
      />
    </Form.Group>
  );
};
